import { useState, type FC } from 'react';

type ResultType = 'verified' | 'need_more' | 'failed' | 'pending';

interface EkycResultScreenProps {
  resultType: ResultType;
  creditScore?: number;
  riskLevel?: 'Tốt' | 'Cần chú ý' | 'Nguy cơ';
  reason?: string;
  role: 'Nông hộ' | 'Hợp tác xã';
  onEnterApp: () => void;
  onRetry: () => void;
  onResubmit: () => void;
  onPendingStay: () => void;
}

function getScoreColor(score: number): string {
  if (score >= 700) return 'text-agrida-600';
  if (score >= 550) return 'text-amber-600';
  return 'text-red-600';
}

function getScoreBg(score: number): string {
  if (score >= 700) return 'bg-agrida-50';
  if (score >= 550) return 'bg-amber-50';
  return 'bg-red-50';
}

function getScoreLabel(score: number): string {
  if (score >= 700) return 'Tốt';
  if (score >= 550) return 'Cần chú ý';
  return 'Nguy cơ';
}

const EkycResultScreen: FC<EkycResultScreenProps> = ({
  resultType,
  creditScore,
  riskLevel,
  reason,
  role,
  onEnterApp,
  onRetry,
  onResubmit,
  onPendingStay,
}) => {
  const [loading, setLoading] = useState(false);

  const handleEnterApp = () => {
    setLoading(true);
    setTimeout(() => onEnterApp(), 300);
  };

  const handleRetry = () => {
    setLoading(true);
    setTimeout(() => onRetry(), 300);
  };

  const handleResubmit = () => {
    setLoading(true);
    setTimeout(() => onResubmit(), 300);
  };

  const handlePendingStay = () => {
    onPendingStay();
  };

  const needMoreReasons = [
    'Ảnh CCCD mặt trước bị mờ, không đọc được số',
    'Thiếu ảnh mặt sau CCCD',
    'Ảnh selfie không khớp với ảnh trên CCCD',
    'Thông tin trên CCCD bị khuất góc, không đọc được ngày cấp',
    'Ảnh chụp bị chói sáng, cần chụp lại trong điều kiện ánh sáng tốt hơn',
  ];

  const displayReason = reason || needMoreReasons[Math.floor(Math.random() * needMoreReasons.length)];

  if (resultType === 'verified') {
    const score = creditScore || Math.floor(Math.random() * 201) + 700;
    const actualRisk = riskLevel || 'Tốt';

    return (
      <div className="flex flex-col items-center pt-2">
        <div className="w-24 h-24 rounded-full bg-agrida-50 flex items-center justify-center mb-4">
          <span className="w-12 h-12 flex items-center justify-center">
            <i className="ri-shield-check-line text-agrida-500 text-4xl" />
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">Xác thực thành công!</h3>
        <p className="text-sm text-gray-500 text-center mb-5">
          {role === 'Nông hộ'
            ? 'Danh tính chủ hộ đã được xác thực. Bạn có thể sử dụng đầy đủ các tính năng ngay bây giờ.'
            : 'Danh tính người đại diện HTX đã được xác thực. Bạn có thể quản lý HTX ngay bây giờ.'}
        </p>

        <div className="w-full bg-white rounded-xl border border-gray-100 p-4 mb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Điểm tín dụng CIC</p>
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-3xl font-extrabold ${getScoreColor(score)}`}>{score}</span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getScoreBg(score)} ${getScoreColor(score)}`}>
              {getScoreLabel(score)}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out ${score >= 700 ? 'bg-agrida-500' : score >= 550 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${Math.max(0, Math.min(100, ((score - 300) / 600) * 100))}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-gray-400">300</span>
            <span className="text-[10px] text-gray-400">900</span>
          </div>
        </div>

        <div className="w-full bg-agrida-50 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-line text-agrida-600" />
            </span>
            <div>
              <p className="text-sm font-medium text-agrida-800 mb-1">Hồ sơ đã được phê duyệt</p>
              <p className="text-xs text-agrida-600">
                Mức độ rủi ro: <strong>{actualRisk}</strong>
              </p>
              <p className="text-xs text-agrida-600">
                Ngày xác thực: <strong>{new Date().toLocaleDateString('vi-VN')}</strong>
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleEnterApp}
          className="w-full bg-agrida-600 hover:bg-agrida-700 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap min-h-[48px] text-sm"
        >
          {loading ? 'Đang chuyển...' : 'Vào ứng dụng'}
        </button>
      </div>
    );
  }

  if (resultType === 'need_more') {
    return (
      <div className="flex flex-col items-center pt-2">
        <div className="w-24 h-24 rounded-full bg-orange-50 flex items-center justify-center mb-4">
          <span className="w-12 h-12 flex items-center justify-center">
            <i className="ri-error-warning-line text-orange-500 text-4xl" />
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">Cần bổ sung thông tin</h3>
        <p className="text-sm text-gray-500 text-center mb-5">
          Hồ sơ cần bổ sung thêm một số thông tin trước khi được phê duyệt.
        </p>

        <div className="w-full bg-orange-50 rounded-xl p-4 mb-5">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-alert-line text-orange-600" />
            </span>
            <div>
              <p className="text-sm font-medium text-orange-800 mb-1">Lý do cần bổ sung</p>
              <p className="text-sm text-orange-700">{displayReason}</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleResubmit}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap min-h-[48px] text-sm"
        >
          {loading ? 'Đang xử lý...' : 'Chụp lại và gửi bổ sung'}
        </button>
      </div>
    );
  }

  if (resultType === 'failed') {
    const failReasons = [
      'Thông tin đăng ký không khớp với CCCD. Tên trên CCCD không trùng khớp.',
      'Ảnh selfie không khớp với ảnh trên CCCD. Vui lòng kiểm tra lại.',
      'CCCD đã hết hạn hoặc không hợp lệ. Vui lòng sử dụng CCCD gắn chip còn hiệu lực.',
      'Không thể xác minh thông tin. Vui lòng thử lại sau.',
    ];

    const displayFailReason = reason || failReasons[Math.floor(Math.random() * failReasons.length)];

    return (
      <div className="flex flex-col items-center pt-2">
        <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <span className="w-12 h-12 flex items-center justify-center">
            <i className="ri-close-circle-line text-red-500 text-4xl" />
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">Xác thực thất bại</h3>
        <p className="text-sm text-gray-500 text-center mb-5">
          Hồ sơ eKYC của bạn không được phê duyệt. Vui lòng kiểm tra lại thông tin.
        </p>

        <div className="w-full bg-red-50 rounded-xl p-4 mb-5">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-600" />
            </span>
            <div>
              <p className="text-sm font-medium text-red-800 mb-1">Lý do từ chối</p>
              <p className="text-sm text-red-700">{displayFailReason}</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleRetry}
          className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap min-h-[48px] text-sm"
        >
          {loading ? 'Đang xử lý...' : 'Thực hiện lại eKYC'}
        </button>
      </div>
    );
  }

  // Pending
  return (
    <div className="flex flex-col items-center pt-2">
      <div className="w-24 h-24 rounded-full bg-amber-50 flex items-center justify-center mb-4">
        <span className="w-12 h-12 flex items-center justify-center">
          <i className="ri-time-line text-amber-500 text-4xl" />
        </span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">Đang chờ xác thực</h3>
      <p className="text-sm text-gray-500 text-center mb-2">
        Hồ sơ của bạn đã được gửi thành công và đang chờ hệ thống xét duyệt.
      </p>
      <p className="text-xs text-gray-400 text-center mb-6">
        Quá trình xét duyệt có thể mất từ <strong>1-2 ngày làm việc</strong>. Bạn sẽ nhận được thông báo khi có kết quả.
      </p>

      <div className="w-full bg-amber-50 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-600" />
          </span>
          <div>
            <p className="text-sm font-medium text-amber-800 mb-1">Lưu ý</p>
            <p className="text-xs text-amber-600">
              Bạn chưa thể sử dụng ứng dụng cho đến khi hồ sơ được phê duyệt. Vui lòng quay lại kiểm tra sau.
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        disabled={loading}
        onClick={handlePendingStay}
        className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap min-h-[48px] text-sm"
      >
        Tôi đã hiểu, quay lại sau
      </button>
    </div>
  );
};

export default EkycResultScreen;